import { PREFLOP_BASELINE_LABEL, PREFLOP_SPOTS, type PreflopSpot } from '../data/preflop'
import { RANKS_DESC, cardPairToIndex, cardToId } from './cards'
import { matrixCellLabel, parseRange } from './range'
import type { Card, SolverResult, StrategyAction, StrategyNode } from '../types/solver'

export type TrainerSourceMode = 'mixed' | 'preflop' | 'postflop' | 'single-result'

export interface PostflopTrainerQuestion {
  kind: 'postflop'
  id: string
  resultId: string
  nodeId: string
  combo: string
}

export interface PreflopTrainerQuestion {
  kind: 'preflop'
  id: string
  spotId: string
  spotLabel: string
  heroPosition: string
  villainPosition?: string
  actionHistory: string
  potBb: number
  effectiveStackBb: number
  handClass: string
  combo: string
  actions: StrategyAction[]
  frequencies: Record<string, number>
  rationale: string
  sourceLabel: string
}

export type TrainerQuestion = PostflopTrainerQuestion | PreflopTrainerQuestion

export interface TrainerActionDetail {
  action: StrategyAction
  frequency: number
  ev?: number
}

export interface TrainerEvaluation {
  verdict: 'correct' | 'close' | 'incorrect'
  selected: TrainerActionDetail
  benchmark: TrainerActionDetail
  mainStrategy: TrainerActionDetail
  evLossBb?: number
  actions: TrainerActionDetail[]
}

export interface TrainerPoolStats {
  preflop: number
  postflop: number
  legacyPostflop: number
  results: number
  strategicResults: number
}

function actionDetails(node: StrategyNode, combo: string): TrainerActionDetail[] {
  const strategy = node.rangeStrategy.combos[combo]
  if (!strategy) return []
  return node.actions.map((action) => {
    const comboAction = strategy.actions.find((candidate) => candidate.actionId === action.id)
    return {
      action,
      frequency: comboAction?.frequency ?? 0,
      ev: comboAction?.ev,
    }
  })
}

export function trainerActionLabel(action: StrategyAction): string {
  const labels: Record<StrategyAction['kind'], string> = {
    fold: 'フォールド',
    check: 'チェック',
    call: 'コール',
    bet: 'ベット',
    raise: 'レイズ',
    'all-in': 'オールイン',
  }
  if (action.label && !/^(Fold|Check|Call|Bet|Raise|All-in)(\s|$)/i.test(action.label)) return action.label
  return action.amountBb === undefined ? labels[action.kind] : `${labels[action.kind]} ${action.amountBb.toFixed(1)}bb`
}

export function buildTrainerQuestions(result: SolverResult, minimumChoices = 3): PostflopTrainerQuestion[] {
  const questions: PostflopTrainerQuestion[] = []
  Object.values(result.nodes).forEach((node) => {
    if (node.actions.length < minimumChoices) return
    Object.entries(node.rangeStrategy.combos).forEach(([combo, strategy]) => {
      if (strategy.reachProbability <= 0) return
      const details = actionDetails(node, combo)
      const finiteEvs = details.filter((detail) => Number.isFinite(detail.ev))
      if (finiteEvs.length < 2 && details.every((detail) => detail.frequency <= 0)) return
      questions.push({
        kind: 'postflop',
        id: `${result.id}:${node.id}:${combo}`,
        resultId: result.id,
        nodeId: node.id,
        combo,
      })
    })
  })
  return questions
}

function handClassCombo(handClass: string, seed: number): string {
  const suits = ['c', 'd', 'h', 's']
  const firstSuit = suits[seed % suits.length]
  const secondSuit = suits[(seed + 1) % suits.length]
  if (handClass.length === 2) return `${handClass[0]}${firstSuit}${handClass[1]}${secondSuit}`
  if (handClass.endsWith('s')) return `${handClass[0]}${firstSuit}${handClass[1]}${firstSuit}`
  return `${handClass[0]}${firstSuit}${handClass[1]}${secondSuit}`
}

const parsedPreflopRanges = new Map<string, Float32Array>()

function frequencyForCombo(range: string, combo: string): number {
  let raw = parsedPreflopRanges.get(range)
  if (!raw) {
    raw = parseRange(range).raw
    parsedPreflopRanges.set(range, raw)
  }
  return raw[cardPairToIndex(cardToId(combo.slice(0, 2)), cardToId(combo.slice(2, 4)))] ?? 0
}

function questionsForPreflopSpot(spot: PreflopSpot, spotIndex: number): PreflopTrainerQuestion[] {
  const foldAction: StrategyAction = { id: 'fold', kind: 'fold', label: 'Fold', frequency: 0 }
  const limpAction: StrategyAction = { id: 'limp', kind: 'call', label: 'リンプ', frequency: 0 }
  const isFirstIn = spot.actions.length === 1 && spot.actions[0].action.kind === 'raise'
  const actions = [...spot.actions.map((entry) => entry.action), ...(isFirstIn ? [limpAction] : []), foldAction]
  const questions: PreflopTrainerQuestion[] = []

  for (let row = 0; row < RANKS_DESC.length; row += 1) {
    for (let col = 0; col < RANKS_DESC.length; col += 1) {
      const handClass = matrixCellLabel(row, col)
      const combo = handClassCombo(handClass, spotIndex + row * 13 + col)
      const frequencies: Record<string, number> = {}
      let nonFoldTotal = 0
      spot.actions.forEach((entry) => {
        const frequency = frequencyForCombo(entry.range, combo)
        frequencies[entry.action.id] = frequency
        nonFoldTotal += frequency
      })

      if (nonFoldTotal > 1) {
        spot.actions.forEach((entry) => { frequencies[entry.action.id] /= nonFoldTotal })
        nonFoldTotal = 1
      }
      frequencies.fold = Math.max(0, 1 - nonFoldTotal)
      if (isFirstIn) frequencies.limp = 0

      questions.push({
        kind: 'preflop',
        id: `preflop:${spot.id}:${handClass}`,
        spotId: spot.id,
        spotLabel: spot.label,
        heroPosition: spot.heroPosition,
        villainPosition: spot.villainPosition,
        actionHistory: spot.actionHistory,
        potBb: spot.potBb,
        effectiveStackBb: spot.effectiveStackBb,
        handClass,
        combo,
        actions,
        frequencies,
        rationale: spot.rationale,
        sourceLabel: PREFLOP_BASELINE_LABEL,
      })
    }
  }
  return questions
}

let cachedPreflopQuestions: PreflopTrainerQuestion[] | null = null

export function buildPreflopQuestions(): PreflopTrainerQuestion[] {
  if (!cachedPreflopQuestions) cachedPreflopQuestions = PREFLOP_SPOTS.flatMap(questionsForPreflopSpot)
  return cachedPreflopQuestions
}

export function shuffleTrainerQuestions<T>(
  questions: T[],
  random: () => number = Math.random,
): T[] {
  const next = [...questions]
  for (let index = next.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1))
    ;[next[index], next[target]] = [next[target], next[index]]
  }
  return next
}

function variedPostflopDeck(results: SolverResult[], random: () => number): PostflopTrainerQuestion[] {
  const groups = shuffleTrainerQuestions(
    results.map((result) => {
      const questions = buildTrainerQuestions(result)
      const difficult = questions.filter((question) => {
        const node = result.nodes[question.nodeId]
        const details = actionDetails(node, question.combo)
        const byFrequency = [...details].sort((a, b) => b.frequency - a.frequency)
        const byEv = details.filter((detail) => Number.isFinite(detail.ev)).sort((a, b) => (b.ev ?? -Infinity) - (a.ev ?? -Infinity))
        const secondFrequency = byFrequency[1]?.frequency ?? 0
        const evGap = byEv.length >= 2 ? (byEv[0].ev ?? 0) - (byEv[1].ev ?? 0) : Infinity
        return secondFrequency >= 0.05 || byFrequency[0]?.frequency < 0.9 || evGap <= 0.05
      })
      const difficultIds = new Set(difficult.map((question) => question.id))
      return [
        ...shuffleTrainerQuestions(difficult, random),
        ...shuffleTrainerQuestions(questions.filter((question) => !difficultIds.has(question.id)), random),
      ]
    }).filter((group) => group.length > 0),
    random,
  )
  const deck: PostflopTrainerQuestion[] = []
  let offset = 0
  while (groups.some((group) => offset < group.length)) {
    groups.forEach((group) => {
      if (offset < group.length) deck.push(group[offset])
    })
    offset += 1
  }
  return deck
}

function variedPreflopDeck(random: () => number): PreflopTrainerQuestion[] {
  const questions = buildPreflopQuestions()
  const mixed = shuffleTrainerQuestions(questions.filter((question) => {
    const values = Object.values(question.frequencies)
    return values.filter((value) => value >= 0.2).length >= 2
  }), random)
  const continueHands = shuffleTrainerQuestions(questions.filter((question) => {
    const main = Object.entries(question.frequencies).sort((a, b) => b[1] - a[1])[0]
    return main?.[0] !== 'fold' && !mixed.some((candidate) => candidate.id === question.id)
  }), random)
  const folds = shuffleTrainerQuestions(questions.filter((question) => {
    const main = Object.entries(question.frequencies).sort((a, b) => b[1] - a[1])[0]
    return main?.[0] === 'fold' && !mixed.some((candidate) => candidate.id === question.id)
  }), random)
  const deck: PreflopTrainerQuestion[] = []
  const max = Math.max(Math.ceil(mixed.length / 2), continueHands.length, folds.length)
  for (let index = 0; index < max; index += 1) {
    if (mixed[index * 2]) deck.push(mixed[index * 2])
    if (mixed[index * 2 + 1]) deck.push(mixed[index * 2 + 1])
    if (continueHands[index]) deck.push(continueHands[index])
    if (folds[index]) deck.push(folds[index])
  }
  return deck
}

export function createTrainerDeck(options: {
  results: SolverResult[]
  mode: TrainerSourceMode
  count: number
  selectedResultId?: string
  recentQuestionIds?: string[]
  random?: () => number
}): TrainerQuestion[] {
  const random = options.random ?? Math.random
  const resultSource = options.mode === 'single-result'
    ? options.results.filter((result) => result.id === options.selectedResultId)
    : options.results
  const postflop = variedPostflopDeck(resultSource, random)
  const preflop = variedPreflopDeck(random)
  const recent = new Set(options.recentQuestionIds ?? [])
  const withoutRecent = <T extends TrainerQuestion>(questions: T[]) => {
    const fresh = questions.filter((question) => !recent.has(question.id))
    return fresh.length >= Math.min(options.count, questions.length) ? fresh : questions
  }
  const freshPostflop = withoutRecent(postflop)
  const freshPreflop = withoutRecent(preflop)
  let deck: TrainerQuestion[]

  if (options.mode === 'preflop') deck = freshPreflop
  else if (options.mode === 'postflop' || options.mode === 'single-result') deck = freshPostflop
  else {
    deck = []
    const target = Math.max(1, options.count)
    let preIndex = 0
    let postIndex = 0
    while (deck.length < target && (preIndex < freshPreflop.length || postIndex < freshPostflop.length)) {
      if (preIndex < freshPreflop.length) deck.push(freshPreflop[preIndex++])
      if (deck.length < target && postIndex < freshPostflop.length) deck.push(freshPostflop[postIndex++])
    }
  }
  return deck.slice(0, Math.max(1, options.count))
}

export function trainerPoolStats(results: SolverResult[]): TrainerPoolStats {
  const strategicCounts = results.map((result) => buildTrainerQuestions(result).length)
  const allCounts = results.map((result) => buildTrainerQuestions(result, 2).length)
  return {
    preflop: buildPreflopQuestions().length,
    postflop: strategicCounts.reduce((sum, count) => sum + count, 0),
    legacyPostflop: allCounts.reduce((sum, count, index) => sum + Math.max(0, count - strategicCounts[index]), 0),
    results: results.length,
    strategicResults: strategicCounts.filter((count) => count > 0).length,
  }
}

export function resultTrainingProfile(result: SolverResult): {
  maximumChoices: number
  eligibleNodes: number
  questions: number
  legacyQuestions: number
} {
  const nodes = Object.values(result.nodes)
  const questions = buildTrainerQuestions(result).length
  const allQuestions = buildTrainerQuestions(result, 2).length
  return {
    maximumChoices: nodes.reduce((maximum, node) => Math.max(maximum, node.actions.length), 0),
    eligibleNodes: nodes.filter((node) => node.actions.length >= 3).length,
    questions,
    legacyQuestions: Math.max(0, allQuestions - questions),
  }
}

function evaluateDetails(actions: TrainerActionDetail[], selectedActionId: string): TrainerEvaluation {
  const selected = actions.find((detail) => detail.action.id === selectedActionId)
  if (!selected) throw new Error('選択したアクションの戦略が見つかりません')
  const mainStrategy = [...actions].sort((a, b) => b.frequency - a.frequency)[0]
  const finiteEvs = actions.filter((detail) => Number.isFinite(detail.ev))
  const benchmark = finiteEvs.length > 0
    ? [...finiteEvs].sort((a, b) => (b.ev ?? -Infinity) - (a.ev ?? -Infinity) || b.frequency - a.frequency)[0]
    : mainStrategy
  const evLossBb = Number.isFinite(selected.ev) && Number.isFinite(benchmark.ev)
    ? Math.max(0, (benchmark.ev ?? 0) - (selected.ev ?? 0))
    : undefined

  let verdict: TrainerEvaluation['verdict']
  if (evLossBb === undefined) {
    verdict = selected.frequency >= 0.4 || selected.frequency >= mainStrategy.frequency - 0.1
      ? 'correct'
      : selected.frequency >= 0.15 ? 'close' : 'incorrect'
  } else if (evLossBb <= 0.01) verdict = 'correct'
  else if (evLossBb <= 0.05) verdict = 'close'
  else verdict = 'incorrect'

  return { verdict, selected, benchmark, mainStrategy, evLossBb, actions }
}

export function evaluateTrainerAnswer(
  node: StrategyNode,
  combo: string,
  selectedActionId: string,
): TrainerEvaluation {
  return evaluateDetails(actionDetails(node, combo), selectedActionId)
}

export function evaluatePreflopAnswer(
  question: PreflopTrainerQuestion,
  selectedActionId: string,
): TrainerEvaluation {
  return evaluateDetails(question.actions.map((action) => ({
    action,
    frequency: question.frequencies[action.id] ?? 0,
  })), selectedActionId)
}

export function trainerQuestionCategory(question: TrainerQuestion, result?: SolverResult, node?: StrategyNode): string {
  if (question.kind === 'preflop') return `preflop:${question.spotId}`
  return `postflop:${node?.street ?? 'unknown'}:${result?.config.trainingContext?.spotId ?? `${result?.config.positions.oop}-${result?.config.positions.ip}`}`
}

export function trainerQuestionCards(question: TrainerQuestion): [Card, Card] {
  return [question.combo.slice(0, 2), question.combo.slice(2, 4)]
}
